import React, { useCallback, useMemo } from 'react';
import { Chip, Box, Autocomplete, Grid, IconButton } from '@mui/material';
import { Check as CheckIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { WcnTextField } from '@/components';
import { equalsIgnoreCase } from '@core/utils';
import { UserApiResponse } from '@/services/userService.ts';
import BaseSelector from '@/components/selectors/BaseSelector.tsx';
import { useApiStore, useSettingsStore } from '@core/store';
import { FieldTooltip } from '@features/administration/components/templates/NotificationTemplatesEditor.tsx';
import { useEmailInput } from '@core/hooks/useEmailInput.ts';

export interface EmailInputRef {
    hasUnsavedEmail: () => boolean;
}

export interface RecipientWithShops {
    eMail: string;
    shopIds: string[];
}

interface EmailInputProps {
    users: UserApiResponse[];
    initialEmails?: RecipientWithShops[];
    onUpdate: (recipients: RecipientWithShops[]) => void;
    onRefReady?: (ref: EmailInputRef) => void;
    onError?: (email: string) => string;
    onWarning?: (email: string) => string;
    disabledEmails?: string[];
    onChipWarning?: (email: string) => string;
}

const EMPTY_RECIPIENTS: RecipientWithShops[] = [];
const EMPTY_USERS: UserApiResponse[] = [];

const CheckboxIcon = ({ checked }: { checked: boolean }) => (
    <span className={`multi-select-checkbox ${checked ? 'checked' : ''}`}>
        {checked && <CheckIcon className="multi-select-checkbox-check" />}
    </span>
);

function EmailInputForShop({
    users = EMPTY_USERS,
    initialEmails = EMPTY_RECIPIENTS,
    onUpdate,
    onRefReady,
    onError,
    onWarning,
    disabledEmails = [],
    onChipWarning
}: EmailInputProps) {
    const { t } = useTranslation();
    const { shopIds } = useApiStore();
    const { isMobile } = useSettingsStore();

    const emailList = useMemo(() => initialEmails.map((r) => r.eMail), [initialEmails]);
    const shopMap = useMemo(() => Object.fromEntries(initialEmails.map((r) => [r.eMail, r.shopIds])), [initialEmails]);
    const recipients = useMemo(() => users.map((u) => u.eMail), [users]);

    const handleRemove = useCallback(
        (email: string) => {
            onUpdate(initialEmails.filter((r) => !equalsIgnoreCase(r.eMail, email)));
        },
        [initialEmails, onUpdate]
    );

    const handleCommit = useCallback(
        (newValue: string[]) => {
            onUpdate(newValue.map((email) => ({ eMail: email, shopIds: shopMap[email] ?? [] })));
        },
        [shopMap, onUpdate]
    );

    const {
        inputValue,
        error,
        warning,
        isOptionDisabled,
        handleKeyDownCapture,
        handleChange,
        handleInputChange,
        clearInput
    } = useEmailInput({
        emailList,
        recipients,
        disabledEmails,
        onError,
        onWarning,
        onRemove: handleRemove,
        onCommit: handleCommit,
        onRefReady
    });

    const checkShopWarning = useCallback(
        (email: string, selectedShops: string[]): boolean => {
            if (selectedShops.length === 0) return false;
            const user = users.find((u) => equalsIgnoreCase(u.eMail, email));
            if (!user) return false;
            if (user.accessToAllStores) return false;
            return selectedShops.some((shopId) => !user.shopIds?.some((id) => equalsIgnoreCase(id, shopId)));
        },
        [users]
    );

    const handleShopChange = useCallback(
        (email: string, newShops: string[]) => {
            onUpdate(initialEmails.map((r) => (r.eMail === email ? { ...r, shopIds: newShops } : r)));
        },
        [initialEmails, onUpdate]
    );

    const hasError = !!error;

    return (
        <Grid container xs={12}>
            <Grid container item xs={12}>
                <Grid item xs={10}>
                    <Autocomplete
                        sx={{ maxWidth: '368px' }}
                        multiple
                        freeSolo
                        disableClearable
                        disableCloseOnSelect
                        filterSelectedOptions={false}
                        options={recipients}
                        value={emailList}
                        inputValue={inputValue}
                        getOptionDisabled={(option) => isOptionDisabled(option as string)}
                        getOptionLabel={(option: any) => {
                            if (!option) return '';
                            return typeof option === 'string' ? option : option.label || '';
                        }}
                        onKeyDownCapture={handleKeyDownCapture}
                        onInputChange={handleInputChange}
                        onChange={handleChange}
                        renderTags={() => null}
                        renderOption={(props, option) => {
                            const { key, className: muiClassName, onClick, ...restProps } = props as any;
                            const isDisabled = isOptionDisabled(option as string);
                            const isSelected = emailList.some(
                                (e) => e.toLowerCase() === (option as string).toLowerCase()
                            );
                            return (
                                <Box
                                    key={key}
                                    component="li"
                                    {...restProps}
                                    onClick={isDisabled ? undefined : onClick}
                                    className={`selector-option multi-select-option${isSelected ? ' selected' : ''}${isDisabled ? ' disabled' : ''}`}
                                >
                                    <CheckboxIcon checked={isSelected} />
                                    <span>{option as string}</span>
                                </Box>
                            );
                        }}
                        renderInput={(params) => (
                            <WcnTextField
                                {...params}
                                label={t('recipientsForSpecificShops')}
                                variant="standard"
                                fullWidth
                                error={hasError}
                                helperText={t(error)}
                                warning={!!warning}
                                warningText={t(warning)}
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: inputValue ? (
                                        <IconButton
                                            size="small"
                                            sx={{
                                                padding: '2px',
                                                color: 'var(--gray-medium)',
                                                '& i': { fontSize: '14px', lineHeight: 1 }
                                            }}
                                            onMouseDown={(e) => {
                                                if (e.button !== 0) return;
                                                e.preventDefault();
                                                clearInput();
                                            }}
                                        >
                                            <i className="bi bi-x-lg" />
                                        </IconButton>
                                    ) : (
                                        params.InputProps.endAdornment
                                    )
                                }}
                            />
                        )}
                    />
                </Grid>
                <Grid
                    item
                    xs={2}
                    sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        alignItems: isMobile ? 'flex-start' : 'center',
                        marginTop: isMobile ? '-8px' : 0
                    }}
                >
                    <FieldTooltip title={t('descriptions.recipientsForSpecificShops')} maxWidth={300} />
                </Grid>
            </Grid>

            <Grid container xs={12} item gap={isMobile ? 2 : 4}>
                {initialEmails.map((recipient, index) => {
                    const { eMail: email, shopIds: selectedShops } = recipient;
                    const chipWarning = onChipWarning?.(email);
                    const shopWarning = checkShopWarning(email, selectedShops);

                    return (
                        <React.Fragment key={`${email}-${index}`}>
                            <Grid container item xs={12} gap={3} sx={{ alignItems: 'flex-start' }}>
                                <Grid item xs={12} sm={6} sx={{ display: 'flex', pt: isMobile ? '14px' : '22px' }}>
                                    <Box sx={{ position: 'relative' }}>
                                        <Chip
                                            label={email}
                                            onDelete={() => handleRemove(email)}
                                            className={`email-chip${chipWarning ? ' email-chip--warning' : ''}`}
                                        />
                                        {chipWarning && (
                                            <Box
                                                className="email-chip-warning-text"
                                                sx={{ position: 'absolute', top: '100%', left: 0, mt: '2px' }}
                                            >
                                                {t(chipWarning)}
                                            </Box>
                                        )}
                                    </Box>
                                </Grid>
                                <Grid item xs={10} sm={5}>
                                    <BaseSelector
                                        multiple
                                        searchable
                                        value={selectedShops}
                                        items={shopIds}
                                        itemText="name"
                                        itemValue="id"
                                        label={t('specificShops')}
                                        onChange={(newShops) => handleShopChange(email, newShops)}
                                        selectDisplayLabels={{
                                            allLabel: t('allShops'),
                                            placeholderLabel: t('selectShop'),
                                            countLabel: t('selectedWithValue')
                                        }}
                                        warning={shopWarning}
                                        warningText={t('validation.shopNotAssignedToUser')}
                                    />
                                </Grid>
                            </Grid>
                        </React.Fragment>
                    );
                })}
            </Grid>
        </Grid>
    );
}

export default EmailInputForShop;
